import { qtcreatorDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import pagesData from "../pagesData";
import { useState } from "react";
import SyntaxHighlighter from "react-syntax-highlighter";

export default function DynamicPage({ pageName }) {
  const [showAll, setShowAll] = useState(false);
  const [copiedStates, setCopiedStates] = useState({});
  const [activeTabs, setActiveTabs] = useState({});

  // Find the page data by name
  const page =
    pagesData.flatMap(section => section.pages).find(p => p.name === pageName);

  if (!page) return <div>Page not found</div>;

  // Copy to clipboard function
  const copyToClipboard = async (code, index) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedStates(prev => ({ ...prev, [index]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [index]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Check if current page is Compatibility
  const isCompatibilityPage = pageName === 'browser-support' || page.title === 'Browser Support' || page.title === 'Compatibility';

  // Prepare table data
  const classData = page.value
    ? Object.entries(page.value).flatMap(([section, classes]) =>
      Object.entries(classes).map(([keyName, styles]) => ({
        section,
        keyName,
        styles,
        comment: "",
      }))
    )
    : [];

  // Get exampleData directly from pagesData
  const exampleData = page.exampleData || [];

  const maxRows = 10;
  const displayedClassData = showAll ? classData : classData.slice(0, maxRows);

  const setActiveTab = (exampleIndex, tabKey) => {
    setActiveTabs(prev => ({
      ...prev,
      [exampleIndex]: tabKey
    }));
  };

  return (
    <>
      <div className="min-h-screen max-w-6xl mx-auto py-10 px-4 lg:px-10 xl:px-28">

        <small className="mb-3 font-mono text-sm/6 font-medium tracking-widest text-color-gray-500 uppercase sm:text-xs/6 dark:text-color-gray-400">
          {pagesData.find(section =>
            section.pages.some(p => p.name === pageName)
          )?.section || ""}
        </small>

        <h1 className="text-3xl font-medium">{page.title}</h1>
        <p className="mt-4 text-color-gray-600 dark:text-color-gray-300">
          {page.description}
        </p>

        {classData.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="mt-14 w-full border-collapse text-left text-color-gray-700">
                <thead>
                  <tr>
                    <th className="text-sm min-w-48 md:min-w-64 pb-2 font-semibold border-b border-color-gray-200 dark:border-color-gray-700 dark:text-color-gray-200">
                      Class
                    </th>
                    <th className="text-sm  pb-2 font-semibold border-b border-color-gray-200 dark:border-color-gray-700 dark:text-color-gray-200">
                      Styles
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayedClassData.map((item, idx) => (
                    <tr key={item.keyName} className="align-top">
                      <td
                        className={`py-3 text-sm text-color-red-600 dark:text-color-red-400${idx === displayedClassData.length - 1
                          ? ""
                          : " border-b border-color-gray-200 dark:border-color-gray-700"
                          }`}
                      >
                        <code>{item.keyName}</code>
                      </td>
                      <td
                        className={`py-3 leading-6 text-sm text-color-emerald-600 dark:text-color-emerald-400${idx === displayedClassData.length - 1
                          ? ""
                          : " border-b border-color-gray-200 dark:border-color-gray-700"
                          }`}
                      >
                        <pre><code>{item.section}{item.section && ': '}{item.styles};</code></pre>
                        {item.comment && (
                          <span className="text-color-gray-400 pl-5 italic">
                            {" "}
                            {item.comment}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {classData.length > maxRows && (
              <div className={`${showAll ? 'sticky bottom-0 z-10 pt-4' : 'relative -mt-10 pt-10'} w-full  pb-4 bg-gradient-[linear-gradient(360deg,#fff,rgba(255,255,255,0.8))] dark:bg-gradient-[linear-gradient(360deg,color-gray-800,color-gray-800/80)]`}>
                <button
                  className={`px-4 py-3 rounded-full bg-color-gray-800 text-color-white dark:bg-color-gray-200 dark:text-color-gray-800 text-sm font-semibold cursor-pointer hover:bg-color-gray-700 dark:hover:bg-color-gray-300 transition`}
                  onClick={() => setShowAll(!showAll)}
                >
                  {showAll ? "Show less" : `Show ${classData.length - maxRows} more classes`}
                </button>
              </div>
            )}
          </>
        )}

        {exampleData.length > 0 && !page.htmlData && (
          <>
            {page.heading !== false && (
              <h2 className="mt-10 text-lg font-semibold text-gray-800">
                Basic example
              </h2>
            )}
            <div className="mt-5 flex flex-col gap-10">
              {exampleData.map((ex, idx) => (
                <div
                  key={ex.title}
                  className="bg-white border border-5 rounded-xl border border-color-gray-100 dark:border-color-gray-700 dark:bg-color-gray-900"
                >
                  <div className="p-6">
                    <p className="text-color-gray-600 text-sm dark:text-color-gray-300">
                      <code className="text-base text-color-gray-800 dark:text-color-white font-semibold">
                        {ex.title}
                      </code>{" "}
                      {ex.description}
                    </p>
                    {ex.moreDescription &&
                      <p className="text-color-gray-600 text-sm dark:text-color-gray-300 mt-4">
                        {ex.moreDescription}
                      </p>
                    }
                    <div className={`mt-4 mx-auto ${ex.parentClass}  bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700`}>
                      {/* Render element dynamically */}
                      {ex.example.element === "img" ? (
                        Array.isArray(ex.example.src) ? (
                          ex.example.src.map((img, i) => (
                            <div className={ex.example.className} key={i}>
                              <p className={img.textClass}>{img.text}</p>
                              {img.divClass && (
                                <div className={img.divClass}>
                                  <div className={img.childDivClass}></div>
                                </div>
                              )}
                              {img.src && (
                                <div className={img.containerClass}>
                                  <img
                                    className={img.className}
                                    src={img.src}
                                    alt={ex.title}
                                  />
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <img
                            className={ex.example.className}
                            src={ex.example.src}
                            alt={ex.title}
                          />
                        )
                      ) : ex.example.element === "iframe" ? (
                        <iframe
                          className={ex.example.className}
                          src={Array.isArray(ex.example.src) ? ex.example.src[0].src : ex.example.src}
                          title={ex.title}
                          allowFullScreen
                        />
                      ) : null}
                    </div>
                    {ex.example.code && !ex.example.tabs && (
                      <div className="lg:max-w-[calc(100vw_-_404px)] max-w-[calc(100vw_-_90px)] mt-4 overflow-x-auto relative leading-5 z-10 rounded-lg bg-color-black scrollbar-[#32404b_#000]">
                        {/* Header with copy button - hide for Compatibility page */}
                        {!isCompatibilityPage && (
                          <div className="flex items-center justify-between px-4 py-2 bg-color-[#23252b]">
                            <span className="text-color-white text-sm font-medium">Code</span>
                            <button 
                              onClick={() => copyToClipboard(ex.example.code, `example-${idx}`)}
                              className="flex gap-1 bg-color-transparent items-center px-2 py-0.5 text-color-white hover:text-color-white/50 transition-all text-sm cursor-pointer"
                            >
                              {copiedStates[`example-${idx}`] ? (
                                <>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                  </svg>
                                  <span>Copied!</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                  </svg>
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                          </div>
                        )}
                        {/* Code content */}
                        <div className="p-3">
                          <SyntaxHighlighter language="java" style={qtcreatorDark}>
                            {ex.example.code}
                          </SyntaxHighlighter>
                        </div>
                      </div>
                    )}
                    {ex.example.tabs && ex.example.tabData && (
                      <div className="lg:max-w-[calc(100vw_-_404px)] max-w-[calc(100vw_-_90px)] mt-4 overflow-x-auto relative leading-5 z-10 rounded-lg bg-color-black scrollbar-[#32404b_#000]">
                        {/* Tab Headers */}
                        <div className="flex items-center gap-4 justify-between px-4 bg-color-[#23252b]">
                          {Object.entries(ex.example.tabData).map(([key, tab]) => {
                            const isActive = activeTabs[idx] === key || (!activeTabs[idx] && key === Object.keys(ex.example.tabData)[0]);
                            return (
                              <button
                                key={key}
                                onClick={() => setActiveTab(idx, key)}
                                className={`px-2 py-4 text-sm cursor-pointer font-medium transition-color bg-color-transparent ${
                                  isActive
                                    ? 'text-color-white  border-b-2 border-color-violet-700'
                                    : 'text-color-gray-400'
                                }`}
                              >
                                {tab.title}
                              </button>
                            );
                          })}
                          
                          {/* Copy button for active tab */}
                          {!isCompatibilityPage && (
                            <div className="ml-auto flex items-center">
                              <button 
                                onClick={() => {
                                  const activeKey = activeTabs[idx] || Object.keys(ex.example.tabData)[0];
                                  const activeContent = ex.example.tabData[activeKey].content;
                                  copyToClipboard(activeContent, `example-tab-${idx}-${activeKey}`);
                                }}
                                className="flex gap-1 bg-color-transparent items-center px-2 py-0.5 text-color-white hover:text-color-white/50 transition-all text-sm cursor-pointer"
                              >
                                {copiedStates[`example-tab-${idx}-${activeTabs[idx] || Object.keys(ex.example.tabData)[0]}`] ? (
                                  <>
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                    </svg>
                                    <span>Copied!</span>
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                    </svg>
                                    <span>Copy</span>
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Tab Content */}
                        <div className="p-3">
                          {Object.entries(ex.example.tabData).map(([key, tab]) => {
                            const isActive = activeTabs[idx] === key || (!activeTabs[idx] && key === Object.keys(ex.example.tabData)[0]);
                            return isActive ? (
                              <div key={key}>
                                {tab.description && (
                                  <p className="text-color-gray-400 text-xs mb-3 italic">{tab.description}</p>
                                )}
                                <SyntaxHighlighter 
                                  language={'html'} 
                                  style={qtcreatorDark}
                                >
                                  {tab.content}
                                </SyntaxHighlighter>
                              </div>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                    <div className="mt-4 text-sm text-color-gray-500 dark:text-color-gray-400" dangerouslySetInnerHTML={{ __html: ex.moreexample }}></div>
                  </div>
                </div>

              ))}
            </div>
          </>
        )}

        {page.htmlData && (
          <div>
            {page.heading !== false && (
              <h2 className="mt-10 text-lg font-semibold text-gray-800">
                Basic example
              </h2>
            )}
            {page.htmlData.map((html, idx) => (
              <div className="mt-5 bg-white border border-5 rounded-xl border border-color-gray-100 dark:border-color-gray-700 dark:bg-color-gray-900" key={idx}>
                <div>
                  <div className="p-6" dangerouslySetInnerHTML={{ __html: html.data }} />
                  {exampleData[idx] && exampleData[idx].example.code && (
                    <div className="p-6">
                      <div className="relative z-10 rounded-lg bg-color-black overflow-hidden">
                        {/* Header with copy button - hide for Compatibility page */}
                        {!isCompatibilityPage && (
                          <div className="flex items-center justify-between px-4 py-2 bg-color-[#23252b]">
                            <span className="text-color-[#f8f8f2] text-sm font-medium">Code</span>
                            <button 
                              onClick={() => copyToClipboard(exampleData[idx].example.code, `htmldata-${idx}`)}
                              className="flex gap-1 bg-color-transparent items-center px-2 py-0.5 text-color-white hover:text-color-white/50 transition-all text-sm cursor-pointer"
                            >
                              {copiedStates[`htmldata-${idx}`] ? (
                                <>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                  </svg>
                                  <span>Copied!</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                  </svg>
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                          </div>
                        )}
                        {/* Code content */}
                        <div className="p-3 text-sm overflow-auto scrollbar-[#32404b_#000]">
                          <SyntaxHighlighter language="java" style={qtcreatorDark}>
                            {exampleData[idx].example.code}
                          </SyntaxHighlighter>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}